import React, { useCallback, useEffect, useRef, useState } from 'react';
import styles from './ResizableSplitPane.module.css';

/* ------------------------------------------------------------------ */
/*  Props                                                              */
/* ------------------------------------------------------------------ */

export interface ResizableSplitPaneProps {
  /** Left (or primary) panel content. */
  left: React.ReactNode;
  /** Right (or secondary) panel content — null/undefined hides it. */
  right?: React.ReactNode | null;
  /** Initial ratio 0-1 for the left panel. Default 0.55. */
  initialRatio?: number;
  /** Minimum width in px for each panel. Default 300. */
  minWidth?: number;
  /** If true, the right panel animates in. */
  animate?: boolean;
  className?: string;
}

/* ------------------------------------------------------------------ */
/*  Component                                                          */
/* ------------------------------------------------------------------ */

export default function ResizableSplitPane({
  left,
  right,
  initialRatio = 0.55,
  minWidth = 300,
  animate = true,
  className,
}: ResizableSplitPaneProps) {
  const containerRef = useRef<HTMLDivElement | null>(null);
  const [ratio, setRatio] = useState(initialRatio);
  const dragRef = useRef<{ dragging: boolean; startX: number; startRatio: number } | null>(null);

  const hasRight = right != null;

  /* ---- Drag handling ---- */
  const beginDrag = useCallback(
    (e: React.MouseEvent) => {
      e.preventDefault();
      dragRef.current = { dragging: true, startX: e.clientX, startRatio: ratio };
    },
    [ratio]
  );

  const onMouseMove = useCallback(
    (e: MouseEvent) => {
      const d = dragRef.current;
      const container = containerRef.current;
      if (!d?.dragging || !container) return;
      const rect = container.getBoundingClientRect();
      const totalW = rect.width;
      if (totalW < 1) return;
      const deltaX = e.clientX - d.startX;
      const deltaPct = deltaX / totalW;
      const minRatio = minWidth / totalW;
      const maxRatio = 1 - minRatio;
      setRatio(Math.max(minRatio, Math.min(maxRatio, d.startRatio + deltaPct)));
    },
    [minWidth]
  );

  const endDrag = useCallback(() => {
    if (dragRef.current) dragRef.current.dragging = false;
  }, []);

  useEffect(() => {
    window.addEventListener('mousemove', onMouseMove);
    window.addEventListener('mouseup', endDrag);
    return () => {
      window.removeEventListener('mousemove', onMouseMove);
      window.removeEventListener('mouseup', endDrag);
    };
  }, [onMouseMove, endDrag]);

  /* ---- Render ---- */
  if (!hasRight) {
    /* No right panel — left fills 100% */
    return (
      <div ref={containerRef} className={`${styles.container} ${className ?? ''}`}>
        <div className={styles.panelFull}>{left}</div>
      </div>
    );
  }

  const leftPct = `${(ratio * 100).toFixed(2)}%`;
  const rightPct = `${((1 - ratio) * 100).toFixed(2)}%`;

  return (
    <div ref={containerRef} className={`${styles.container} ${className ?? ''}`}>
      <div
        className={styles.panelLeft}
        style={{ width: leftPct, transition: animate ? 'width 0.25s ease' : undefined }}
      >
        {left}
      </div>

      <div className={styles.divider} onMouseDown={beginDrag}>
        <div className={styles.dividerGrip} />
      </div>

      <div
        className={styles.panelRight}
        style={{ width: rightPct, transition: animate ? 'width 0.25s ease' : undefined }}
      >
        {right}
      </div>
    </div>
  );
}
